import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { FirebaseAppContext } from 'libs.firebase_react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ErrorUtils, versionNumberToString } from 'libs.react';
import { ApiClientService, useApiClient, AuthContext, useProductTemplates, } from 'libs.react_internal';
import { DEPLOYMENT_STATUS } from '../../constants/deployment.constants';
import { LogEvents } from '../../constants/log_events.constants';
import { StudyConfigurationContext } from '../../contexts/study_configuration/study_configuration.context';
import { useJobChecker } from '../../hooks/use_job_checker/use_job_checker.hook';
export const useStudyUpgrade = ({ onModalClose }) => {
    const momClient = useApiClient(ApiClientService.MOM);
    const [isLoading, setIsLoading] = useState(false);
    const { currentStudy, studyId, assignStudy, configId, getStudy } = useContext(StudyConfigurationContext);
    const { entityId } = useContext(AuthContext);
    const translate = useLocalizeMessage();
    const { logEvent } = useContext(FirebaseAppContext);
    const queryClient = useContext(QueryClientContext);
    const { checkJobStatus } = useJobChecker();
    const { addNotification } = useToastNotification();
    const { validatedTemplate } = useProductTemplates();
    const publishStudyVersion = async ({ versionName, versionDescription, isLatestVersion, studyConfigId, }) => {
        const upgradeTime = isLatestVersion ? 'post' : 'pre';
        const { data: response } = await momClient.post(`/v1/entities/${entityId}/studies/${studyId}/publish`, {
            spec: currentStudy.toRosetta(),
            studyConfigId,
            metadata: {
                description: versionDescription,
                name: versionName,
            },
        }, { no404Handling: true });
        const statusResponse = await checkJobStatus(response.data.jobId);
        if (statusResponse.status === DEPLOYMENT_STATUS.COMPLETED) {
            logEvent(LogEvents.UPGRADE_STUDY_UPGRADE_VERSION_CREATED(upgradeTime), {
                study_id: studyId,
                version_name: versionName,
                version_description: versionDescription,
            });
        }
        else {
            const errorMessage = ErrorUtils.getResultErrorMessage(statusResponse.result);
            let toastMessage;
            if (errorMessage) {
                toastMessage = translate('Study software template updated successfully, however we were unable to generate new configuration versions due to the following errors:\n\n{errorMessage}\n\nPlease update the study configuration to resolve this error and proceed with creating versions on the latest software.', { errorMessage });
            }
            else {
                toastMessage = translate('Study software template updated successfully, however something went wrong when attempting to generate new configuration versions. Please update the study configuration to resolve potential issues and proceed with creating versions on the latest software.');
            }
            addNotification({
                title: translate('Update successful'),
                subtitle: toastMessage,
                type: 'warning',
            });
            logEvent(LogEvents.UPGRADE_STUDY_UPGRADE_VERSION_CREATION_FAILED(upgradeTime), {
                study_id: studyId,
                version_name: versionName,
                version_description: versionDescription,
            });
            return false;
        }
        return true;
    };
    const upgradeStudy = async () => {
        if (!isLoading) {
            setIsLoading(true);
            const latestTemplate = `R${versionNumberToString(validatedTemplate)}`;
            const currentTemplate = `R${versionNumberToString(currentStudy.productTemplate)}`;
            const preUpgradeReports = currentStudy.reports || [];
            let versionName = translate('{currentTemplate} build (auto-created)', { currentTemplate });
            let versionDescription = translate('Auto-created version of pre-upgrade study configuration on {currentTemplate}', { currentTemplate });
            try {
                // Creating the version of the study configuration on the old software version
                const isOldVersionCreated = await publishStudyVersion({
                    versionName,
                    versionDescription,
                    studyConfigId: configId,
                    isLatestVersion: false,
                });
                if (isOldVersionCreated) {
                    const { newStudyConfigId } = await getStudy();
                    if (newStudyConfigId) {
                        // Creating the version of the study configuration on the latest software version
                        currentStudy.productTemplate = validatedTemplate;
                        versionName = translate('{latestTemplate} build (auto-created)', { latestTemplate });
                        versionDescription = translate('Auto-created version of study configuration on {latestTemplate}', {
                            latestTemplate,
                        });
                        const isNewVersionCreated = await publishStudyVersion({
                            versionName,
                            versionDescription,
                            isLatestVersion: true,
                            studyConfigId: newStudyConfigId,
                        });
                        if (isNewVersionCreated) {
                            addNotification({
                                title: translate('Update successful'),
                                subtitle: translate('System generated versions were created to reflect configuration before and after the update.'),
                                type: 'success',
                            });
                            onModalClose();
                        }
                    }
                }
            }
            catch (error) {
                // @ts-expect-error - AxiosError type is not being caught by instanceof
                if (error.name === 'AxiosError' && !error.response?.data?.errorHandled) {
                    const message = error.response?.data?.error;
                    const subtitle = message
                        ? translate('The version of your study build could not be saved: \n{message}', { message })
                        : translate('The version of your study build could not be saved. Please try again.');
                    addNotification({ title: translate('Error creating study version'), subtitle, type: 'error' });
                }
                logEvent(LogEvents.VERSION_CREATION_FAILED, {
                    study_id: studyId,
                    version_name: versionName,
                    version_description: versionDescription,
                });
            }
            finally {
                // we assign the study in finally because no matter the outcome of the old and new version creations, we still want to update the template
                const { newStudy } = await getStudy();
                if (newStudy && newStudy.productTemplate !== validatedTemplate) {
                    newStudy.productTemplate = validatedTemplate;
                    // Same reports restoration as above — see comment in the try block.
                    if (newStudy.reports.length === 0 && preUpgradeReports.length > 0) {
                        newStudy.reports = preUpgradeReports;
                    }
                    assignStudy(newStudy);
                }
                else if (currentStudy.productTemplate !== validatedTemplate) {
                    currentStudy.productTemplate = validatedTemplate;
                    assignStudy(currentStudy);
                }
                setIsLoading(false);
                await queryClient?.invalidateQueries({ queryKey: ['study-configs', studyId] });
            }
        }
    };
    return {
        upgradeStudy,
        isLoading,
    };
};
